import React, { FC, useEffect, useState } from 'react';
import { Grid, useTheme, useMediaQuery, Typography, Tooltip } from '@mui/material';
import { CorporateFare, InfoOutlined } from '@mui/icons-material';
import '../../App.css';
import Card from '../general/Card';
import { formatISODate } from '../../common/utils/common.util';
import { Box } from 'components/index';
import { DrawSummary } from './project.type';
import CustomizedTextField from './CustomizedTextField';
import DynamicDatePicker from '../common/DynamicDatePicker';

type ProjectDrawSummaryProps = {
  drawSummary: DrawSummary;
  headerAction?: JSX.Element;
  errorFieldNames: string[];
  readonly?: boolean;
  isEditMode?: boolean;
  onEditChange?: (field: string, value: any, target: 'drawSummary' | 'budgetSummary') => void;
  assignedLineItemsCount?: number;
  totalLineItemsCount?: number;
};
const ProjectDrawSummary: FC<ProjectDrawSummaryProps> = ({
  drawSummary,
  headerAction,
  errorFieldNames,
  readonly,
  isEditMode,
  onEditChange,
  assignedLineItemsCount,
  totalLineItemsCount,
}) => {
  const theme = useTheme();
  const isScreenBelowMedium = useMediaQuery(theme.breakpoints.down('md'));
  const handleEditChange = (field: string, value: any) => {
    onEditChange && onEditChange(field, value, 'drawSummary');
  };

  // Local drafts so keystrokes update the fields instantly without pushing into the shared
  // project state (and re-rendering the whole page) on every keystroke. Only committed via
  // handleEditChange on blur. Re-synced when switching draws or when the value changes externally.
  const [nameDraft, setNameDraft] = useState(drawSummary?.name || '');
  const [applicationNumberDraft, setApplicationNumberDraft] = useState(drawSummary?.applicationNumber || '');

  useEffect(() => {
    setNameDraft(drawSummary?.name || '');
    setApplicationNumberDraft(drawSummary?.applicationNumber || '');
  }, [drawSummary?.id, drawSummary?.name, drawSummary?.applicationNumber]);

  return (
    <Box>
      <Grid container spacing={3} sx={{ marginBottom: 2 }}>
        <Grid size={{ xs: 12 }}>
          <Card
            icon={<CorporateFare color='primary' />}
            heading='G702/3 Information'
            data={{
              Name: isEditMode ? (
                <CustomizedTextField
                  placeholder='e.g., Draw 1'
                  value={nameDraft}
                  onChange={e => setNameDraft(e.target.value)}
                  onBlur={() => handleEditChange('name', nameDraft)}
                  type='text'
                  hasError={errorFieldNames.includes('Name')}
                />
              ) : (
                drawSummary?.name || '--'
              ),
              'Application Number': isEditMode ? (
                <CustomizedTextField
                  placeholder='e.g., 1'
                  value={applicationNumberDraft}
                  onChange={e => setApplicationNumberDraft(e.target.value)}
                  onBlur={() => handleEditChange('applicationNumber', applicationNumberDraft)}
                  type='text'
                  hasError={errorFieldNames.includes('Application Number')}
                />
              ) : (
                drawSummary?.applicationNumber || '--'
              ),
              'Period Beginning': isEditMode ? (
                <DynamicDatePicker
                  fieldName='periodBeginning'
                  value={drawSummary?.periodBeginning ?? null}
                  onChange={handleEditChange}
                  hasError={errorFieldNames.includes('Period Beginning')}
                />
              ) : drawSummary?.periodBeginning ? (
                formatISODate(drawSummary?.periodBeginning)
              ) : (
                '--'
              ),
              'Period Ending': isEditMode ? (
                <DynamicDatePicker
                  fieldName='periodEnding'
                  value={drawSummary?.periodEnding ?? null}
                  onChange={handleEditChange}
                  hasError={errorFieldNames.includes('Period Ending')}
                />
              ) : drawSummary?.periodEnding ? (
                formatISODate(drawSummary?.periodEnding)
              ) : (
                '--'
              ),
              'Divisions Assigned':
                totalLineItemsCount != null && assignedLineItemsCount != null && totalLineItemsCount > 0 ? (
                  <Typography variant='body2' fontWeight='regular' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {Math.round((assignedLineItemsCount / totalLineItemsCount) * 100)}%
                    <Tooltip title={`${assignedLineItemsCount} / ${totalLineItemsCount} line items assigned`} arrow>
                      <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary', cursor: 'default' }} />
                    </Tooltip>
                  </Typography>
                ) : (
                  '--'
                ),
            }}
            itemsPerRow={isScreenBelowMedium ? 2 : 6}
            headerAction={headerAction}
            errorFields={errorFieldNames}
            disableActions={readonly}
            isEditMode={isEditMode}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProjectDrawSummary;
